import { useState, useEffect, useRef, useCallback } from "react";
import { ArrowUp } from "lucide-react";

export function ScrollToTop() {
  const [isVisible, setIsVisible] = useState(false);
  const [isOverDarkBg, setIsOverDarkBg] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const checkBackground = useCallback(() => {
    if (!buttonRef.current) return;
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const buttonCenterX = buttonRect.left + buttonRect.width / 2;
    const buttonCenterY = buttonRect.top + buttonRect.height / 2;
    
    // Temporarily hide button to get element behind it
    const originalVisibility = buttonRef.current.style.visibility;
    buttonRef.current.style.visibility = 'hidden';
    
    const elementBehind = document.elementFromPoint(buttonCenterX, buttonCenterY);
    
    buttonRef.current.style.visibility = originalVisibility;
    
    if (elementBehind) {
      // Check if element or its parents have dark background (primary color)
      let current: Element | null = elementBehind;
      while (current) {
        const styles = window.getComputedStyle(current);
        const bgColor = styles.backgroundColor;
        
        // Check for primary blue color (hsl values around 217-222 hue)
        if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
          const match = bgColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
          if (match) {
            const [, r, g, b] = match.map(Number);
            // Detect dark blue backgrounds (primary color)
            if (r < 100 && g < 100 && b > 150) {
              setIsOverDarkBg(true);
              return;
            }
          }
        }
        current = current.parentElement;
      }
    }
    setIsOverDarkBg(false);
  }, []);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.scrollY > 400) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
      checkBackground();
    };

    window.addEventListener("scroll", toggleVisibility, { passive: true });
    toggleVisibility();

    return () => window.removeEventListener("scroll", toggleVisibility);
  }, [checkBackground]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <button
      ref={buttonRef}
      onClick={scrollToTop}
      className={`fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center ${
        isOverDarkBg 
          ? "bg-white text-primary" 
          : "bg-primary text-primary-foreground"
      } ${
        isVisible
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-4 pointer-events-none"
      }`}
      aria-label="Наверх"
    >
      <ArrowUp className="w-5 h-5" />
    </button>
  );
}
